import ExcelJS from 'exceljs';
import { sanitizeExcelWorkbook } from '_utils/sanitize-spreadsheet-cell';

// Builds a downloadable .xlsx that mirrors the on-screen SLA Summary Report:
// one worksheet, a title/airport/date-range header, then every section as a
// KPI table with the same columns and the same green/red/grey score
// highlighting used by KpiRow in the summary components. The client fills the
// DAA monthly KPI sheet straight from these numbers, so the export must match
// the screen 1:1 — the rows handed in here are the exact objects the summary
// components render.

// Score-cell colours, kept in ARGB to match the hex values used by KpiRow.
const SCORE_STYLES = {
    pass: { fill: 'FFDCFCE7', font: 'FF166534' },   // #dcfce7 / #166534
    fail: { fill: 'FFFEE2E2', font: 'FF991B1B' },   // #fee2e2 / #991b1b
    none: { fill: 'FFF9FAFB', font: 'FF6B7280' },   // #f9fafb / #6b7280 (total === 0)
};

const HEADER_FILL = 'FFF9FAFB';   // table header row (matches on-screen #f9fafb)
const HEADER_FONT = 'FF374151';   // #374151
const SECTION_FILL = 'FFEAF4FB';  // section title band
const SECTION_FONT = 'FF1F2937';  // #1f2937

const THIN_BORDER = {
    top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
    left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
    bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
    right: { style: 'thin', color: { argb: 'FFE5E7EB' } },
};

const COLUMN_HEADERS = ['Overall Summary', 'Minutes', 'Target', 'Total Pass', 'Total', 'Score'];

const parseTarget = (target) => {
    if (!target) return 0;
    return parseFloat(String(target).replace('%', '')) || 0;
};

const setBorders = (row) => {
    row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = THIN_BORDER;
    });
};

/**
 * @param {Object} params
 * @param {Array<{label: string, hideMinutes?: boolean, rows: Array}>} params.sections
 *   Each row: { title, minutes, target, totalPass, total, score }.
 * @param {string} params.iata
 * @param {string} params.startDate  YYYY-MM-DD
 * @param {string} params.endDate    YYYY-MM-DD
 */
export const exportSlaSummaryExcel = async ({ sections, iata, startDate, endDate }) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('SLA Summary');
    worksheet.properties.showGridLines = false;

    worksheet.columns = [
        { width: 48 }, // Overall Summary
        { width: 12 }, // Minutes
        { width: 12 }, // Target
        { width: 14 }, // Total Pass
        { width: 12 }, // Total
        { width: 12 }, // Score
    ];

    // ----- Title block -----
    const titleRow = worksheet.addRow(['SLA Summary Report']);
    worksheet.mergeCells(`A${titleRow.number}:F${titleRow.number}`);
    titleRow.getCell(1).font = { size: 16, bold: true, color: { argb: 'FF1F2937' } };
    titleRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
    titleRow.height = 24;

    const dateLabel = startDate === endDate ? startDate : `${startDate} to ${endDate}`;
    const metaRow = worksheet.addRow([`Airport: ${iata || '—'}    Period: ${dateLabel}`]);
    worksheet.mergeCells(`A${metaRow.number}:F${metaRow.number}`);
    metaRow.getCell(1).font = { size: 11, color: { argb: 'FF6B7280' } };
    worksheet.addRow([]); // spacer

    // ----- Sections -----
    sections.forEach((section) => {
        if (!section?.rows?.length) return;

        // Section title band
        const sectionRow = worksheet.addRow([section.label]);
        worksheet.mergeCells(`A${sectionRow.number}:F${sectionRow.number}`);
        sectionRow.getCell(1).font = { bold: true, size: 12, color: { argb: SECTION_FONT } };
        sectionRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SECTION_FILL } };
        sectionRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
        sectionRow.height = 20;

        // Column header row
        const headerRow = worksheet.addRow(COLUMN_HEADERS);
        headerRow.eachCell((cell, col) => {
            cell.font = { bold: true, color: { argb: HEADER_FONT } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } };
            cell.alignment = { horizontal: col === 1 ? 'left' : 'center', vertical: 'middle' };
        });
        setBorders(headerRow);

        // Data rows
        section.rows.forEach((kpi) => {
            const total = kpi.total ?? 0;
            const meetsTarget = kpi.score >= parseTarget(kpi.target);
            const scoreState = total === 0 ? 'none' : meetsTarget ? 'pass' : 'fail';
            const minutesText = section.hideMinutes ? '–' : `${kpi.minutes} min`;
            const scoreText = total === 0 ? '-' : `${kpi.score}%`;

            const row = worksheet.addRow([
                kpi.title,
                minutesText,
                kpi.target || '–',
                kpi.totalPass ?? 0,
                total,
                scoreText,
            ]);

            row.getCell(1).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
            for (let c = 2; c <= 6; c++) {
                row.getCell(c).alignment = { horizontal: 'center', vertical: 'middle' };
            }

            const scoreCell = row.getCell(6);
            const style = SCORE_STYLES[scoreState];
            scoreCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: style.fill } };
            scoreCell.font = { bold: true, color: { argb: style.font } };

            setBorders(row);
        });

        worksheet.addRow([]); // spacer between sections
    });

    // ----- Download -----
    sanitizeExcelWorkbook(workbook);
    const buffer = await workbook.xlsx.writeBuffer();
    const downloadUrl = window.URL.createObjectURL(new Blob([buffer]));
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', `SLA-Summary-Report-${iata || 'NA'}-${startDate}-${endDate}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    return null;
};
